import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { memo, useEffect } from 'react';

import { useAlgoTileId } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { AlgoOrderCreationTile } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/index.tsx';
import { AlgoOrderEditingTile } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/index.tsx';
import { AlgoOrderExecutedTile } from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/index.tsx';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import type { AlgoOrderType } from '@/store/orders/state.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export type AlgoTileProps = {
  algoType: AlgoOrderType;
};

/*
 * Memorized AlgoTile component because the Grid rerenders a lot.
 */
export const AlgoOrderTile = memo((props: AlgoTileProps) => {
  const { algoType } = props;
  const tileId = useAlgoTileId();

  const dispatch = useAppDispatch();
  const userPreferences = useAppSelector((state) => state.userPreferences);

  const { status, mode, errorCode } = useAppSelector((state) => {
    const tile = selectAlgoTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        status: 'creating',
      };
    }

    return {
      status: tile.properties.status.type,
      mode: tile.properties.mode,
      errorCode: tile.properties.status.type === 'failed' ? tile.properties.status.errorMessage : undefined,
    };
  }, deepEqual);

  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (status === 'pending') {
    logger.logInformation(`[ORDER] Tile with id ${tileId} is pending From state.`);
    return <ExecutionPendingOrderStatus isEditing={mode === 'EDIT'} />;
  }

  if (status === 'failed') {
    const handleReset = () => {
      dispatch(ordersSlice.actions.resetOrder({ tileId, userPreferences }));
    };

    return (
      <ErrorTile
        tileId={tileId}
        onReset={handleReset}
        errorCode={errorCode}
        quoteId={tileId}
        errorDescription={
          errorCode === 'order.timeout' ? 'order.timeout.error.message.description' : 'order.error.message.description'
        }
        errorTitle={errorCode === 'order.timeout' ? 'order.timeout.error.message.title' : 'order.error.message.title'}
      />
    );
  }

  if (mode === 'EDIT') {
    return <AlgoOrderEditingTile algoType={algoType} />;
  }

  if (status === 'creating') {
    return <AlgoOrderCreationTile algoType={algoType} />;
  }

  return <AlgoOrderExecutedTile algoType={algoType} />;
});
